import { useCallback, useId, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useLatestLoad } from '../../hooks/useLatestLoad';
import { useSubmit } from '../../hooks/useSubmit';
import { correctCustomerCredit, fetchCustomerCredit } from '../adminApi';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { Skeleton } from '../../ui/Skeleton';
import { Spinner } from '../../ui/Spinner';
import { btnPrimary, btnSecondary, inputCls } from '../ui/styles';
import { received } from '../inbox/inboxRules';
import { useRequestId } from '../../hooks/useRequestId';
import { ledgerKindLabel, signedAmount, unitBalance } from '../vouchers/voucherLabels';
import { money, parseEuroInput } from '../../core/format/currency';
import { GIFTCARD_MAX_CENTS } from '../../core/rules/kassa';
import { BIG_TOPUP_CENTS } from '../ui/topUpLimits';
import type { RequestOptions } from '../../core/types/backend';
import type { LedgerUnit } from '../../core/types/vouchers';

/**
 * Tegoed van één klant: euro's en behandelingen apart (nooit bij elkaar opgeteld), met de laatste
 * bewegingen en een correctie met verplichte reden. Transactie: wachten op de server, met requestId.
 */
export function CreditPanel() {
  const {
    id = ''
  } = useParams();
  const load = useCallback((opts: RequestOptions) => fetchCustomerCredit(id, opts), [id]);
  const {
    data,
    setData,
    status,
    reload
  } = useLatestLoad(load);
  const [open, setOpen] = useState(false);
  return <Panel title="Tegoed" actions={data && !open ? <button type="button" onClick={() => setOpen(true)} className="text-[13px] text-ink underline underline-offset-4 hover:text-clay" data-id="element-1145" data-mp="6szpd4iuav2d">
            Corrigeren
          </button> : undefined} data-id="element-1144" data-mp="1sv6x8ydfq9i">
      {status === 'error' && !data ? <div className="text-[14px] text-muted" data-id="element-1146" data-mp="1i7dc261ux4gt">
          Kon het tegoed niet laden.{' '}
          <button type="button" onClick={reload} className="text-ink underline underline-offset-4" data-id="element-1147" data-mp="1n5eq2ckfedlf">
            Opnieuw
          </button>
        </div> : !data ? <div role="status" aria-label="Tegoed laden" className="space-y-2" data-id="element-1148" data-mp="skiugrqhg1m8">
          <Skeleton className="h-5 w-24 rounded" data-id="element-1149" data-mp="17gqglxlgbd45" />
          <Skeleton className="h-5 w-32 rounded" data-id="element-1150" data-mp="17gi2y3hi83rw" />
        </div> : <>
          <dl className="grid grid-cols-2 gap-3" data-id="element-1151" data-mp="12j8eaq1xvw54">
            <div className="rounded-md bg-sand px-3 py-2.5" data-id="element-1152" data-mp="esci7gqgaes2">
              <dt className="text-[12px] text-muted" data-id="element-1153" data-mp="esm7t37l2jx8">In euro’s</dt>
              <dd className="text-[16px] tabular-nums text-ink" data-id="element-1154" data-mp="9u19j9bw6abf">{unitBalance('cents', data.balance.cents)}</dd>
            </div>
            <div className="rounded-md bg-sand px-3 py-2.5" data-id="element-1155" data-mp="ojp7c0kv6que">
              <dt className="text-[12px] text-muted" data-id="element-1156" data-mp="jr5vb81ts3rp">Behandelingen</dt>
              <dd className="text-[16px] tabular-nums text-ink" data-id="element-1157" data-mp="3s794ji8l60i5">{data.balance.treatment}</dd>
            </div>
          </dl>

          {open && <CorrectionForm customerId={id} onClose={() => setOpen(false)} onDone={next => {
        setData(next);
        setOpen(false);
        toast.success('Tegoed gecorrigeerd');
      }} data-id="element-1158" data-mp="3n7cyldgbvyzy" />}

          {data.ledger.length > 0 ? <ol className="mt-4 divide-y divide-line border-t border-line text-[13px]" data-id="element-1159" data-mp="1sputwady3802">
              {[...data.ledger].sort((a, b) => b.at.localeCompare(a.at)).slice(0, 6).map(e => <li key={e.id} className="flex items-start justify-between gap-3 py-2.5" data-id="element-1160" data-mp="2qg7s5r5h8l4c">
                    <span className="min-w-0" data-id="element-1161" data-mp="2cgg9ve7xgikj">
                      <span className="block text-ink" data-id="element-1162" data-mp="2cfncv4yrt7fl">{ledgerKindLabel[e.kind]}</span>
                      <span className="block truncate text-muted" data-id="element-1163" data-mp="2gfxgwc305nrt">
                        {received(e.at)}
                        {e.note ? ` · ${e.note}` : ''}
                      </span>
                    </span>
                    <span className="shrink-0 tabular-nums text-ink" data-id="element-1164" data-mp="153o88lblltrd">{signedAmount(e.unit, e.amount)}</span>
                  </li>)}
            </ol> : <p className="mt-4 text-[13px] text-muted" data-id="element-1165" data-mp="1ghzrji5oytyj">Nog geen tegoed.</p>}
        </>}
    </Panel>;
}
type FormProps = {
  customerId: string;
  onClose: () => void;
  onDone: (next: Awaited<ReturnType<typeof correctCustomerCredit>>) => void;
};
function CorrectionForm({
  customerId,
  onClose,
  onDone
}: FormProps) {
  const amountId = useId();
  const reasonId = useId();
  const [unit, setUnit] = useState<LedgerUnit>('cents');
  const [direction, setDirection] = useState<1 | -1>(1);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const {
    submit,
    submitting,
    error,
    fieldErrors
  } = useSubmit(correctCustomerCredit);
  const raw = unit === 'cents' ? parseEuroInput(amount) : /^\d+$/.test(amount.trim()) ? Number(amount.trim()) : null;
  const euroUp = unit === 'cents' && direction === 1;
  const amountProblem = raw === null || raw <= 0 ? unit === 'cents' ? 'Vul een bedrag in, bijv. 15 of 12,50.' : 'Vul een aantal in, bijv. 1.' : euroUp && raw > GIFTCARD_MAX_CENTS ? `Maximaal ${money(GIFTCARD_MAX_CENTS)} erbij.` : null;
  const reasonProblem = reason.trim() ? null : 'Een reden is verplicht; die staat in de geschiedenis.';
  const value = raw === null ? 0 : raw * direction;
  const attempt = `${unit}|${value}|${reason.trim()}`;
  const requestId = useRequestId(attempt);
  // Veel euro's erbij: eerst in het formulier zelf bevestigen. Een ander bedrag vraagt opnieuw.
  const big = euroUp && raw !== null && raw > BIG_TOPUP_CENTS;
  const [confirmedFor, setConfirmedFor] = useState<string | null>(null);
  const [asking, setAsking] = useState<string | null>(null);
  const needsConfirm = big && confirmedFor !== attempt;
  const showConfirm = needsConfirm && asking === attempt;
  const amountError = fieldErrors.amount ?? (tried ? amountProblem : null);
  const reasonError = fieldErrors.reason ?? (tried ? reasonProblem : null);
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (amountProblem) return document.getElementById(amountId)?.focus();
    if (reasonProblem) return document.getElementById(reasonId)?.focus();
    if (needsConfirm) {
      if (asking !== attempt) return setAsking(attempt);
      setConfirmedFor(attempt);
    }
    const next = await submit(customerId, {
      requestId,
      unit,
      amount: value,
      reason: reason.trim()
    });
    if (next) onDone(next);
  };
  return <form onSubmit={e => void onSubmit(e)} noValidate className="mt-4 space-y-4 rounded-md bg-shell p-3.5" data-id="element-1166" data-mp="3nvm1h4t0vo9u">
      <div className="grid grid-cols-2 gap-2" data-id="element-1167" data-mp="3jt8xaktxbyf6">
        <label className="block" data-id="element-1168" data-mp="3jvlm9hy3n91q">
          <span className="sr-only" data-id="element-1169" data-mp="1ncoy947jf1d">Erbij of eraf</span>
          <select value={direction} onChange={e => setDirection(e.target.value === '-1' ? -1 : 1)} className={inputCls} data-id="element-1170" data-mp="1o5mqkl6uu7d">
            <option value="1" data-id="element-1171" data-mp="gal4tn8go8jo">Erbij</option>
            <option value="-1" data-id="element-1172" data-mp="gbmilzgv3953">Eraf</option>
          </select>
        </label>
        <label className="block" data-id="element-1173" data-mp="la7gvtcjziqw">
          <span className="sr-only" data-id="element-1174" data-mp="l95u4rm6d7tc">Eenheid</span>
          <select value={unit} onChange={e => setUnit(e.target.value === 'treatment' ? 'treatment' : 'cents')} className={inputCls} data-id="element-1175" data-mp="6cnhr32c8hsn">
            <option value="cents" data-id="element-1176" data-mp="bagjnjwlypn4">Euro’s</option>
            <option value="treatment" data-id="element-1177" data-mp="bdb4vdng4ehl">Behandelingen</option>
          </select>
        </label>
      </div>
      <Field label={unit === 'cents' ? 'Bedrag (€)' : 'Aantal behandelingen'} data-id="element-1178" data-mp="2atlr83381yt">
        <input id={amountId} inputMode={unit === 'cents' ? 'decimal' : 'numeric'} value={amount} onChange={e => setAmount(e.target.value)} aria-invalid={amountError ? true : undefined} aria-describedby={amountError ? `${amountId}-error` : undefined} className={`${inputCls} tabular-nums`} data-id="element-1179" data-mp="2cwliju1l9gq" />
      </Field>
      {amountError && <p id={`${amountId}-error`} className="-mt-2 text-[12px] text-clay" data-id="element-1180" data-mp="3eu1h4a9mfnrq">
          {amountError}
        </p>}
      <Field label="Reden" hint="Bijvoorbeeld: overgezet uit Salonized, of compensatie na een fout." data-id="element-1181" data-mp="3q8wgei9t12aa">
        <input id={reasonId} value={reason} onChange={e => setReason(e.target.value)} maxLength={200} aria-invalid={reasonError ? true : undefined} aria-describedby={reasonError ? `${reasonId}-error` : undefined} className={inputCls} data-id="element-1182" data-mp="3l90lr9mzyn7e" />
      </Field>
      {reasonError && <p id={`${reasonId}-error`} className="-mt-2 text-[12px] text-clay" data-id="element-1183" data-mp="37gu6dpro4k90">
          {reasonError}
        </p>}
      {error && !fieldErrors.amount && !fieldErrors.reason && <p role="alert" className="rounded-md bg-blush/50 px-3 py-2 text-[13px] text-ink" data-id="element-1184" data-mp="2xlir96cclb0q">
          {error}
        </p>}
      {showConfirm && <p role="alert" className="rounded-md bg-blush/50 px-3 py-2 text-[13px] text-ink" data-id="element-1185" data-mp="2ojj3zwaqpapc">
          Weet je het zeker? {money(value)} erbij.
        </p>}
      <div className="flex flex-wrap gap-2" data-id="element-1186" data-mp="143bd6udvdpvj">
        <button type="submit" disabled={submitting} aria-busy={submitting} className={btnPrimary} data-id="element-1187" data-mp="182ankuideeud">
          {submitting && <Spinner data-id="element-1188" data-mp="1d2yhbnxzh9sa" />}
          {submitting ? 'Bezig…' : showConfirm ? `Ja, ${money(value)} erbij` : raw && !amountProblem ? `${signedAmount(unit, value)} opslaan` : 'Opslaan'}
        </button>
        <button type="button" onClick={onClose} disabled={submitting} className={btnSecondary} data-id="element-1189" data-mp="ke10lp9f7l1n">
          Niet corrigeren
        </button>
      </div>
    </form>;
}